/**
 * <kk-favorites-page> — behaviour for sections/kk-favorites.liquid
 *
 * - Reads the saved handles from window.KKFavorites (assets/kk-favorites.js).
 * - Fetches each product's card from /products/<handle>?view=kk-card (templates/product.kk-card.liquid)
 *   so the page uses exactly the same KK product card as the rest of the store.
 * - Products that no longer exist are dropped from favorites.
 * - Un-hearting a card removes it; the empty state shows when nothing is left.
 * - "Add all products to cart" adds the first available variant of each (sold-out ones skipped).
 */
const ROOT = window.Shopify?.routes?.root || '/';
const STORAGE_KEY = 'kk:favorites';

class KKFavoritesPage extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.grid = this.querySelector('[data-kk-favorites-grid]');
    this.empty = this.querySelector('[data-kk-favorites-empty]');
    this.loading = this.querySelector('[data-kk-favorites-loading]');
    this.addAll = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-kk-favorites-add-all]'));
    this.status = this.querySelector('[data-kk-favorites-status]');

    this.addAll?.addEventListener('click', () => this.#addAllToCart(), { signal });
    document.addEventListener('kk:favorites:updated', (event) => this.#sync(event.detail?.handles ?? []), { signal });

    // kk-favorites.js is a separate module; it has always run by DOMContentLoaded.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#load(), { once: true, signal });
    } else {
      this.#load();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  #handles() {
    if (window.KKFavorites) return window.KKFavorites.list();
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }

  async #load() {
    const handles = this.#handles();
    if (!handles.length) {
      this.#render([]);
      return;
    }

    this.isLoading = true;
    const cards = await Promise.all(handles.map((handle) => this.#fetchCard(handle)));
    const html = [];
    const missing = [];
    cards.forEach((card, index) => {
      if (card) html.push({ handle: handles[index], card });
      else missing.push(handles[index]);
    });
    this.#render(html);
    this.isLoading = false;

    // Products removed or unpublished since they were saved.
    missing.forEach((handle) => window.KKFavorites?.remove(handle));
  }

  async #fetchCard(handle) {
    try {
      const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}?view=kk-card`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) return null;
      const text = (await response.text()).trim();
      return text.includes('kk-product-card') ? text : null;
    } catch {
      return null;
    }
  }

  /** @param {{ handle: string, card: string }[]} items */
  #render(items) {
    if (this.loading) this.loading.hidden = true;
    if (!this.grid) return;

    const fragment = document.createDocumentFragment();
    for (const { handle, card } of items) {
      const li = document.createElement('li');
      li.dataset.handle = handle;
      // Markup comes from our own theme template (same-origin Liquid render).
      li.innerHTML = card;
      fragment.append(li);
    }
    this.grid.replaceChildren(fragment);
    this.#updateState();
  }

  /** Remove cards whose product was un-favorited (heart tapped on this page or in another tab). */
  #sync(handles) {
    if (!this.grid || this.isLoading) return;
    const keep = new Set(handles);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const li of [...this.grid.children]) {
      if (keep.has(/** @type {HTMLElement} */ (li).dataset.handle || '')) continue;
      if (reduced) {
        li.remove();
      } else {
        li.setAttribute('data-removing', '');
        setTimeout(() => {
          li.remove();
          this.#updateState();
        }, 300);
      }
    }
    this.#updateState();

    // A product favorited elsewhere while this page is open: reload to include it.
    const shown = new Set([...this.grid.children].map((li) => /** @type {HTMLElement} */ (li).dataset.handle));
    if (handles.some((handle) => !shown.has(handle))) this.#load();
  }

  #updateState() {
    const remaining = this.grid ? [...this.grid.children].filter((li) => !li.hasAttribute('data-removing')).length : 0;
    if (this.empty) this.empty.hidden = remaining > 0;
    if (this.addAll) this.addAll.hidden = remaining === 0;
  }

  async #addAllToCart() {
    if (!this.addAll || !window.KKFavorites) return;
    this.addAll.setAttribute('aria-busy', 'true');
    this.addAll.disabled = true;

    try {
      const { added, skipped } = await window.KKFavorites.addAllToCart();
      const parts = [];
      if (added) parts.push((this.dataset.addedText || 'Added [count] to your snack bag.').replace('[count]', String(added)));
      if (skipped) parts.push((this.dataset.skippedText || '[count] sold out, skipped.').replace('[count]', String(skipped)));
      this.#say(parts.join(' '));
    } catch (error) {
      this.#say(error?.message || 'Something went wrong. Please try again.');
    } finally {
      this.addAll.removeAttribute('aria-busy');
      this.addAll.disabled = false;
    }
  }

  #say(message) {
    if (!this.status) return;
    this.status.textContent = message;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => (this.status.textContent = ''), 6000);
  }
}

if (!customElements.get('kk-favorites-page')) {
  customElements.define('kk-favorites-page', KKFavoritesPage);
}
